import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import MDSpinner from "react-md-spinner";
import {
  FaClipboardList,
  FaCalendarAlt,
  FaInbox,
  FaPlus,
  FaCheckCircle,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaEdit,
  FaSearch,
  FaTimes,
  FaMapMarkedAlt,
  FaList,
  FaUserClock,
} from "react-icons/fa";

import { usePendingSchedules } from "../../contexts/PendingSchedulesContext";
import ScheduledBadge, {
  getObsCardClass,
  ObservationExecutionInfo,
} from "../Operation/ScheduledBadge";
import { hasExecutedPeriod } from "../../helpers/observationDateUtils";
import myConfig from "../../configs/config";
import CreateScheduleModal from "./CreateScheduleModal";
import ServiceOrderCardEditModal from "./ServiceOrderCardEditModal";
import ServiceOrderCompleteModal from "./ServiceOrderCompleteModal";
import {
  EMPTY_SERVICE_ORDER_FILTERS,
  collectFilterOptions,
  hasActiveFilters,
  observationFarmNames,
  observationMatchesFilters,
  selectedLandPlots,
} from "./serviceOrderListFilters";
import ServiceOrderInfoButton from "./ServiceOrderInfoButton";
import ServiceOrderDayReports from "./ServiceOrderDayReports";
import ServiceOrderLandplotMapModal from "./ServiceOrderLandplotMapModal";
import FieldAppHarvestLossOrderResults from "../FieldApp/FieldAppHarvestLossOrderResults";
import FieldAppPlantingCalibrationOrderResults from "../FieldApp/FieldAppPlantingCalibrationOrderResults";
import {
  serviceOrderTypeLabel,
  showsFertilizationFields,
  showsHarvestLossFields,
  showsPlantingFields,
} from "../../constants/serviceOrderTypes";
import { sortObservationsByRecency } from "./serviceOrderListSort";
import { ServiceOrderProductsWithCopy } from "./ServiceOrderProductsPanel";
import {
  formatHectares,
  serviceOrderWorkProgress,
} from "./serviceOrderWorkProgress";
import "./ServiceOrderProductsPanel.css";
import "./PendingSchedules.css";
import "../FieldApp/FieldAppHarvestLossOrderResults.css";
import "../Operation/Operation.css";

function ServiceOrderHarvestLossResults({
  farmId,
  obs,
  t,
  defaultExpanded = false,
}) {
  if (!showsHarvestLossFields(obs.service_type)) return null;

  return (
    <FieldAppHarvestLossOrderResults
      farmId={farmId}
      observationId={obs.id}
      serviceOrderPlan={obs.service_order_plan}
      serviceOrderPlanPublished={obs.service_order_plan_published}
      orderLabel={
        obs.operationName
          ? `${obs.operationName} (#${obs.id})`
          : `#${obs.id}`
      }
      selection={{}}
      defaultExpanded={defaultExpanded}
      t={t}
    />
  );
}

function ServiceOrderPlantingResults({
  farmId,
  obs,
  t,
  defaultExpanded = false,
}) {
  const isPlanting = showsPlantingFields(obs.service_type);
  const isFertilization = showsFertilizationFields(obs.service_type);
  if (!isPlanting && !isFertilization) return null;

  return (
    <FieldAppPlantingCalibrationOrderResults
      farmId={farmId}
      observationId={obs.id}
      variant={isFertilization ? "fertilization" : "planting"}
      orderLabel={
        obs.operationName
          ? `${obs.operationName} (#${obs.id})`
          : `#${obs.id}`
      }
      selection={{}}
      defaultExpanded={defaultExpanded}
      t={t}
    />
  );
}

function PendingSchedules({ t }) {
  const { farm_id } = useParams();
  const location = useLocation();
  const {
    pendingObs,
    completedObs,
    loading,
    completedLoading,
    error,
    completedError,
    refreshPending,
    refreshCompleted,
  } = usePendingSchedules();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [completingObs, setCompletingObs] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [newestFirst, setNewestFirst] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [workView, setWorkView] = useState("all");
  /** orders = ordens de serviço; reports = relatos do operador */
  const [listScope, setListScope] = useState("orders");
  const [plotsOpen, setPlotsOpen] = useState(false);
  const [showPlotMap, setShowPlotMap] = useState(false);
  const [filters, setFilters] = useState(EMPTY_SERVICE_ORDER_FILTERS);

  const focusObsId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("obs");
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [location.search]);

  const expandHarvestLoss = useMemo(
    () => new URLSearchParams(location.search).get("expand_loss") === "1",
    [location.search]
  );
  const expandPlanting = useMemo(
    () =>
      new URLSearchParams(location.search).get("expand_plantio") === "1" ||
      new URLSearchParams(location.search).get("expand_fertilizacao") === "1",
    [location.search]
  );

  useEffect(() => {
    document.title = "Agriconnected - Ordens de Serviço";
  }, []);

  useEffect(() => {
    if (!focusObsId) return;
    const inCompleted = completedObs.some(
      (obs) => Number(obs.id) === focusObsId
    );
    const inPending = pendingObs.some((obs) => Number(obs.id) === focusObsId);
    if (inCompleted) {
      setActiveTab("completed");
    } else if (inPending) {
      setActiveTab("pending");
    }
  }, [focusObsId, completedObs, pendingObs]);

  useEffect(() => {
    if (!focusObsId) return undefined;
    if (loading || (activeTab === "completed" && completedLoading)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const element = document.getElementById(
        `pending-schedules-obs-${focusObsId}`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 320);
    return () => window.clearTimeout(timer);
  }, [
    focusObsId,
    loading,
    completedLoading,
    activeTab,
    pendingObs,
    completedObs,
  ]);

  const filterOptions = useMemo(
    () => collectFilterOptions([...pendingObs, ...completedObs]),
    [pendingObs, completedObs]
  );
  const plotChoices = useMemo(() => {
    const orders = filters.farm
      ? [...pendingObs, ...completedObs].filter((obs) =>
          observationFarmNames(obs).some(
            (name) => name.toLowerCase() === String(filters.farm).toLowerCase()
          )
        )
      : [...pendingObs, ...completedObs];
    const names = collectFilterOptions(orders).landPlots.slice();
    selectedLandPlots(filters).forEach((name) => {
      if (!names.includes(name)) names.push(name);
    });
    return names.sort((left, right) => left.localeCompare(right, "pt-BR"));
  }, [completedObs, filters, pendingObs]);
  const filtersActive = hasActiveFilters(filters);
  const filteredPendingObs = useMemo(
    () =>
      pendingObs.filter((obs) => observationMatchesFilters(obs, filters)),
    [pendingObs, filters]
  );
  const filteredCompletedObs = useMemo(
    () =>
      completedObs.filter((obs) => observationMatchesFilters(obs, filters)),
    [completedObs, filters]
  );
  const allObservations = useMemo(
    () => [...pendingObs, ...completedObs],
    [pendingObs, completedObs]
  );
  const sortedPendingObs = sortObservationsByRecency(
    filteredPendingObs,
    newestFirst
  );
  const sortedCompletedObs = sortObservationsByRecency(
    filteredCompletedObs,
    newestFirst
  );

  const handleAskComplete = useCallback((obs) => {
    setCompletingObs(obs);
  }, []);

  const handleComplete = useCallback(
    async ({ executed_start_date, executed_end_date }) => {
      if (!completingObs) return;
      try {
        await axios.post(
          `${myConfig.API_URL}/observation/${completingObs.id}/complete/`,
          { executed_start_date, executed_end_date },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("client-token")}`,
            },
          }
        );
        setCompletingObs(null);
        refreshPending();
      } catch (err) {
        const detail =
          err &&
          err.response &&
          err.response.data &&
          (err.response.data.detail || err.response.data.executed_end_date);
        toast.error(
          (Array.isArray(detail) ? detail[0] : detail) ||
            t("Não foi possível concluir a ordem. Tente novamente.")
        );
        throw err;
      }
    },
    [completingObs, refreshPending]
  );

  const openEdit = useCallback((obs) => {
    setShowCreateModal(false);
    setEditingOrder(obs);
  }, []);

  const handleCardEditSave = useCallback(
    async (payload) => {
      if (!editingOrder) return;
      await axios.patch(
        `${myConfig.API_URL}/observation/${editingOrder.id}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      setEditingOrder(null);
      toast.success(t("Alterações da ordem foram salvas."));
      refreshPending();
      refreshCompleted();
    },
    [editingOrder, refreshPending, refreshCompleted, t]
  );

  const updateFilter = useCallback((field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_SERVICE_ORDER_FILTERS);
  }, []);

  const pickLandPlots = useCallback((plots) => {
    const names = (plots || []).map((plot) => plot && plot.name).filter(Boolean);
    const farms = Array.from(
      new Set((plots || []).map((plot) => plot && plot.farmName).filter(Boolean))
    );
    setFilters((current) => ({
      ...current,
      landPlots: names,
      farm: farms.length === 1 ? farms[0] : current.farm,
    }));
    setShowFilters(true);
    setShowPlotMap(false);
  }, []);

  const toggleLandPlot = useCallback((name) => {
    setFilters((current) => {
      const selected = selectedLandPlots(current);
      const next = selected.includes(name)
        ? selected.filter((item) => item !== name)
        : selected.concat(name);
      return { ...current, landPlots: next };
    });
  }, []);

  const isLoading =
    activeTab === "pending"
      ? loading
      : completedLoading && completedObs.length === 0;

  if (isLoading) {
    return (
      <div className="pending-schedules-page">
        <div style={{ textAlign: "center", paddingTop: "60px" }}>
          <MDSpinner size={48} />
        </div>
      </div>
    );
  }

  const tabError = activeTab === "pending" ? error : completedError;

  return (
    <div className="pending-schedules-page">
      <div className="pending-schedules-header">
        <div className="pending-schedules-header__main">
          <FaClipboardList className="pending-schedules-header__icon" />
          <h3 className="pending-schedules-header__title">
            {t("Ordens de Serviço")}
          </h3>
          {activeTab === "pending" && pendingObs.length > 0 && (
            <span className="pending-schedules-total-badge">
              {filtersActive
                ? `${filteredPendingObs.length}/${pendingObs.length}`
                : pendingObs.length}
            </span>
          )}
          {activeTab === "completed" && completedObs.length > 0 && (
            <span className="pending-schedules-total-badge pending-schedules-total-badge--completed">
              {filtersActive
                ? `${filteredCompletedObs.length}/${completedObs.length}`
                : completedObs.length}
            </span>
          )}
          <button
            type="button"
            className={`pending-schedules-search-toggle${
              showFilters || filtersActive
                ? " pending-schedules-search-toggle--active"
                : ""
            }`}
            onClick={() => setShowFilters((current) => !current)}
            title={t("Filtrar busca")}
            aria-expanded={showFilters}
            aria-label={t("Filtrar busca")}
          >
            <FaSearch />
          </button>
          <div className="pending-schedules-view-toggle" role="group" aria-label={t("Tipo de trabalho")}>
            <button
              type="button"
              className={`pending-schedules-search-toggle${
                workView === "scheduled" ? " pending-schedules-search-toggle--active" : ""
              }`}
              aria-pressed={workView === "scheduled"}
              title={t("Ordens agendadas")}
              aria-label={t("Ordens agendadas")}
              onClick={() => {
                setWorkView("scheduled");
                setActiveTab("pending");
              }}
            >
              <FaCalendarAlt />
            </button>
            <button
              type="button"
              className={`pending-schedules-search-toggle${
                workView === "executed" ? " pending-schedules-search-toggle--active" : ""
              }`}
              aria-pressed={workView === "executed"}
              title={t("Trabalhos executados")}
              aria-label={t("Trabalhos executados")}
              onClick={() => {
                setWorkView("executed");
                setActiveTab("completed");
              }}
            >
              <FaCheckCircle />
            </button>
            <button
              type="button"
              className={`pending-schedules-search-toggle${
                workView === "all" ? " pending-schedules-search-toggle--active" : ""
              }`}
              aria-pressed={workView === "all"}
              title={t("Agendadas e executadas")}
              aria-label={t("Agendadas e executadas")}
              onClick={() => setWorkView("all")}
            >
              <FaList />
            </button>
          </div>
        </div>
        <div className="pending-schedules-create-actions">
          <ServiceOrderInfoButton t={t} />
          <button
            type="button"
            className="btn btn-success pending-schedules-create-btn"
            onClick={() => {
              setEditingOrder(null);
              setShowCreateModal(true);
            }}
          >
            <FaPlus style={{ marginRight: 6 }} />
            {t("Nova Ordem de Serviço")}
          </button>
        </div>
      </div>

      <div className="pending-schedules-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={listScope === "orders" && activeTab === "pending"}
          className={`pending-schedules-tab${
            listScope === "orders" && activeTab === "pending"
              ? " pending-schedules-tab--active"
              : ""
          }`}
          onClick={() => {
            setListScope("orders");
            setActiveTab("pending");
            if (workView === "executed") setWorkView("scheduled");
          }}
        >
          <FaCalendarAlt style={{ marginRight: 6 }} />
          {t("Pendentes")} ({filteredPendingObs.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listScope === "orders" && activeTab === "completed"}
          className={`pending-schedules-tab${
            listScope === "orders" && activeTab === "completed"
              ? " pending-schedules-tab--active"
              : ""
          }`}
          onClick={() => {
            setListScope("orders");
            setActiveTab("completed");
            if (workView === "scheduled") setWorkView("executed");
          }}
        >
          <FaCheckCircle style={{ marginRight: 6 }} />
          {t("Concluídas")} ({filteredCompletedObs.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listScope === "orders"}
          className={`pending-schedules-tab${
            listScope === "orders" ? " pending-schedules-tab--scope" : ""
          }`}
          title={t("Ordens de serviço")}
          aria-label={t("Ordens de serviço")}
          onClick={() => setListScope("orders")}
        >
          <FaClipboardList style={{ marginRight: 6 }} />
          {t("Ordens")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listScope === "reports"}
          className={`pending-schedules-tab${
            listScope === "reports" ? " pending-schedules-tab--active" : ""
          }`}
          title={t("Relatos do operador")}
          aria-label={t("Relatos do operador")}
          onClick={() => setListScope("reports")}
        >
          <FaUserClock style={{ marginRight: 6 }} />
          {t("Relatos")}
        </button>
        <button
          type="button"
          className="pending-schedules-sort-btn"
          onClick={() => setNewestFirst((current) => !current)}
          title={
            newestFirst
              ? t("Mais recentes em cima. Clique para inverter.")
              : t("Mais antigas em cima. Clique para inverter.")
          }
        >
          {newestFirst ? (
            <FaSortAmountDown style={{ marginRight: 6 }} />
          ) : (
            <FaSortAmountUp style={{ marginRight: 6 }} />
          )}
          {newestFirst ? t("Mais recentes") : t("Mais antigas")}
        </button>
      </div>

      {showFilters ? (
        <div className="pending-schedules-filters">
          <div className="pending-schedules-filters__field pending-schedules-filters__field--query">
            <label htmlFor="pending-schedules-filter-query">{t("Buscar")}</label>
            <input
              id="pending-schedules-filter-query"
              type="search"
              className="form-control"
              value={filters.query}
              onChange={(event) => updateFilter("query", event.target.value)}
              placeholder={t("Buscar por texto, talhão ou produto")}
            />
          </div>
          <div className="pending-schedules-filters__columns">
            <div className="pending-schedules-filters__column">
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-operator">
                  {t("Operador")}
                </label>
                <select
                  id="pending-schedules-filter-operator"
                  className="form-control"
                  value={filters.operator}
                  onChange={(event) => updateFilter("operator", event.target.value)}
                >
                  <option value="">{t("Todos os operadores")}</option>
                  {filterOptions.operators.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-machine">
                  {t("Máquina")}
                </label>
                <select
                  id="pending-schedules-filter-machine"
                  className="form-control"
                  value={filters.machine}
                  onChange={(event) => updateFilter("machine", event.target.value)}
                >
                  <option value="">{t("Todas as máquinas")}</option>
                  {filterOptions.machines.map((ident) => (
                    <option key={ident} value={ident}>
                      {ident}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-title">{t("Título")}</label>
                <input
                  id="pending-schedules-filter-title"
                  type="search"
                  className="form-control"
                  value={filters.title}
                  onChange={(event) => updateFilter("title", event.target.value)}
                  placeholder={t("Título da ordem ou do relato")}
                />
              </div>
            </div>
            <div className="pending-schedules-filters__column">
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-farm">{t("Fazenda")}</label>
                <select
                  id="pending-schedules-filter-farm"
                  className="form-control"
                  value={filters.farm}
                  onChange={(event) => updateFilter("farm", event.target.value)}
                >
                  <option value="">{t("Todas as fazendas")}</option>
                  {filterOptions.farms.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pending-schedules-filters__field pending-schedules-filters__plot-wrap">
                <span id="pending-schedules-filter-plot-label">{t("Talhão")}</span>
                <button
                  type="button"
                  className="form-control pending-schedules-filters__plot-toggle"
                  aria-expanded={plotsOpen}
                  aria-labelledby="pending-schedules-filter-plot-label"
                  onClick={() => setPlotsOpen((current) => !current)}
                >
                  {selectedLandPlots(filters).length
                    ? `${selectedLandPlots(filters).length} ${t("talhões marcados")}`
                    : t("Todos os talhões")}
                </button>
                {plotsOpen ? (
                  <div
                    className="pending-schedules-filters__plots"
                    role="group"
                    aria-labelledby="pending-schedules-filter-plot-label"
                  >
                    {plotChoices.length === 0 ? (
                      <p className="pending-schedules-filters__plots-empty">
                        {t("Nenhum talhão na lista. Escolha no mapa.")}
                      </p>
                    ) : (
                      plotChoices.map((name) => (
                        <label key={name} className="pending-schedules-filters__plot-option">
                          <input
                            type="checkbox"
                            checked={selectedLandPlots(filters).includes(name)}
                            onChange={() => toggleLandPlot(name)}
                          />
                          <span>{name}</span>
                        </label>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="btn btn-default pending-schedules-filters__map-btn"
                onClick={() => {
                  setPlotsOpen(false);
                  setShowPlotMap(true);
                }}
              >
                <FaMapMarkedAlt style={{ marginRight: 6 }} />
                {t("Escolher talhão no mapa")}
              </button>
            </div>
            <div className="pending-schedules-filters__column">
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-from">{t("Data inicial")}</label>
                <input
                  id="pending-schedules-filter-from"
                  type="date"
                  className="form-control"
                  value={filters.dateFrom}
                  onChange={(event) => updateFilter("dateFrom", event.target.value)}
                />
              </div>
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-to">{t("Data final")}</label>
                <input
                  id="pending-schedules-filter-to"
                  type="date"
                  className="form-control"
                  value={filters.dateTo}
                  onChange={(event) => updateFilter("dateTo", event.target.value)}
                />
              </div>
              <div className="pending-schedules-filters__field">
                <label htmlFor="pending-schedules-filter-product">{t("Produto")}</label>
                <input
                  id="pending-schedules-filter-product"
                  type="search"
                  className="form-control"
                  value={filters.product}
                  list="pending-schedules-product-options"
                  onChange={(event) => updateFilter("product", event.target.value)}
                  placeholder={t("Nome do produto")}
                />
                <datalist id="pending-schedules-product-options">
                  {filterOptions.products.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>
          {filtersActive ? (
            <button
              type="button"
              className="btn btn-default pending-schedules-filters__clear"
              onClick={clearFilters}
            >
              <FaTimes style={{ marginRight: 6 }} />
              {t("Limpar filtros")}
            </button>
          ) : null}
        </div>
      ) : null}

      {listScope === "reports" ? (
        <ServiceOrderDayReports
          farmId={farm_id}
          filters={filters}
          t={t}
          observations={allObservations}
          onEdit={openEdit}
        />
      ) : null}
      <ServiceOrderLandplotMapModal
        show={showPlotMap}
        farmId={farm_id}
        farmName={filters.farm}
        selectedNames={selectedLandPlots(filters)}
        onHide={() => setShowPlotMap(false)}
        onPick={pickLandPlots}
        t={t}
      />

      {tabError && (
        <div className="alert alert-danger pending-schedules-error">
          {t("Erro ao carregar ordens de serviço.")}{" "}
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={
              activeTab === "pending" ? refreshPending : refreshCompleted
            }
          >
            {t("Tentar novamente")}
          </button>
        </div>
      )}

      <CreateScheduleModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        farmId={farm_id}
        onCreated={refreshPending}
        t={t}
      />
      <ServiceOrderCardEditModal
        show={Boolean(editingOrder)}
        obs={editingOrder}
        onHide={() => setEditingOrder(null)}
        onSave={handleCardEditSave}
        t={t}
      />
      <ServiceOrderCompleteModal
        show={Boolean(completingObs)}
        obs={completingObs}
        onHide={() => setCompletingObs(null)}
        onConfirm={handleComplete}
        t={t}
      />

      {listScope === "orders" &&
      (workView === "scheduled" ||
        (workView === "all" && activeTab === "pending")) &&
      !tabError &&
      pendingObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaInbox className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem de serviço pendente.")}</p>
        </div>
      ) : listScope === "orders" &&
        (workView === "scheduled" ||
          (workView === "all" && activeTab === "pending")) &&
        !tabError &&
        filtersActive &&
        filteredPendingObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaSearch className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem pendente corresponde aos filtros.")}</p>
        </div>
      ) : listScope === "orders" &&
        (workView === "scheduled" ||
          (workView === "all" && activeTab === "pending")) &&
        !tabError ? (
        <>
          <section>
            {sortedPendingObs.map((obs) => (
              <PendingObsCard
                key={obs.id}
                obs={obs}
                farm_id={farm_id}
                onAskComplete={handleAskComplete}
                onEdit={openEdit}
                focusObsId={focusObsId}
                expandHarvestLoss={expandHarvestLoss}
                expandPlanting={expandPlanting}
                t={t}
              />
            ))}
          </section>
        </>
      ) : null}

      {listScope === "orders" &&
      (workView === "executed" ||
        (workView === "all" && activeTab === "completed")) &&
      !tabError &&
      completedObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaCheckCircle className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem de serviço concluída ainda.")}</p>
        </div>
      ) : listScope === "orders" &&
        (workView === "executed" ||
          (workView === "all" && activeTab === "completed")) &&
        !tabError &&
        filtersActive &&
        filteredCompletedObs.length === 0 ? (
        <div className="pending-schedules-empty">
          <FaSearch className="pending-schedules-empty__icon" />
          <p>{t("Nenhuma ordem concluída corresponde aos filtros.")}</p>
        </div>
      ) : listScope === "orders" &&
        (workView === "executed" ||
          (workView === "all" && activeTab === "completed")) &&
        !tabError ? (
        <section>
          <div className="pending-schedules-group-header pending-schedules-group-header--completed">
            <FaCheckCircle style={{ marginRight: "6px" }} />
            {t("Serviços executados")} ({filteredCompletedObs.length})
          </div>
          {sortedCompletedObs.map((obs) => (
            <CompletedObsCard
              key={obs.id}
              obs={obs}
              farm_id={farm_id}
              onEdit={openEdit}
              onAskComplete={handleAskComplete}
              focusObsId={focusObsId}
              expandHarvestLoss={expandHarvestLoss}
              expandPlanting={expandPlanting}
              t={t}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function ServiceOrderLocationInfo({ obs, t }) {
  const farmLabel = obs.service_farm_names || obs.site_farm_name;
  if (!farmLabel && !obs.land_plot_names && !obs.audio_message_url) return null;
  return (
    <div className="pending-schedules-card__location">
      {farmLabel ? (
        <span>
          <strong>{t("Fazenda")}:</strong> {farmLabel}
        </span>
      ) : null}
      {obs.land_plot_names ? (
        <span>
          <strong>{t("Talhões")}:</strong> {obs.land_plot_names}
        </span>
      ) : null}
      {obs.track_heading_bearing != null ? (
        <span>
          <strong>{t("Rumo A→B")}:</strong>{" "}
          {Number(obs.track_heading_bearing).toFixed(2)}°
        </span>
      ) : null}
      {obs.track_heading_point_a_lat != null &&
      obs.track_heading_point_a_lng != null ? (
        <span className="pending-schedules-card__track-heading-coords">
          <strong>{t("Ponto A")}:</strong>{" "}
          {Number(obs.track_heading_point_a_lat).toFixed(6)},{" "}
          {Number(obs.track_heading_point_a_lng).toFixed(6)}
        </span>
      ) : null}
      {obs.track_heading_point_b_lat != null &&
      obs.track_heading_point_b_lng != null ? (
        <span className="pending-schedules-card__track-heading-coords">
          <strong>{t("Ponto B")}:</strong>{" "}
          {Number(obs.track_heading_point_b_lat).toFixed(6)},{" "}
          {Number(obs.track_heading_point_b_lng).toFixed(6)}
        </span>
      ) : null}
      {obs.track_heading_on_farm_map ? (
        <span>
          <strong>{t("Mapa da fazenda")}:</strong> {t("Rumo A-B publicado")}
        </span>
      ) : null}
      {obs.track_heading_summary ? (
        <span className="pending-schedules-card__track-heading">
          <strong>{t("Rumo de pista")}:</strong>{" "}
          {obs.track_heading_summary.split("\n")[0]}
        </span>
      ) : null}
      {obs.audio_message_url ? (
        <div className="pending-schedules-card__audio">
          <strong>{t("Áudio")}:</strong>
          <audio controls preload="metadata" src={obs.audio_message_url} />
        </div>
      ) : null}
      {obs.track_heading_audio_url ? (
        <div className="pending-schedules-card__audio">
          <strong>{t("Áudio do rumo")}:</strong>
          <audio controls preload="metadata" src={obs.track_heading_audio_url} />
        </div>
      ) : null}
    </div>
  );
}

function ServiceOrderCardActions({
  obs,
  farm_id,
  onEdit,
  onAskComplete,
  showComplete,
  t,
}) {
  const history = useHistory();
  const progress = serviceOrderWorkProgress(obs);
  const trackingHint = hasExecutedPeriod(obs)
    ? t("Ver rastreamento do período executado")
    : t("Ver rastreamento do período solicitado");

  const openOperationMap = () => {
    if (!progress.hasTrailTarget || !progress.fromDay || !progress.toDay) return;
    const params = new URLSearchParams({
      tab: "tracking",
      from: progress.fromDay,
      to: progress.toDay,
    });
    if (progress.machineIdents.length) {
      params.set("machines", progress.machineIdents.join(","));
    }
    if (progress.operatorIds.length) {
      params.set("operators", progress.operatorIds.join(","));
    }
    history.push(`/${farm_id}/field-app/?${params.toString()}`);
  };

  return (
    <div className="pending-schedules-card__actions">
      <button
        type="button"
        className="btn btn-sm btn-default"
        onClick={() => onEdit(obs)}
      >
        <FaEdit style={{ marginRight: 6 }} />
        {t("Editar")}
      </button>
      {showComplete ? (
        <button
          type="button"
          className="btn btn-sm btn-success"
          onClick={() => onAskComplete(obs)}
        >
          <FaCheckCircle style={{ marginRight: 6 }} />
          {t("Concluir")}
        </button>
      ) : null}
      {obs.operationId ? (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}?filter_obs=${obs.id}`}
          className="btn btn-sm btn-outline-success pending-schedules-tracking-btn"
          title={trackingHint}
        >
          <FaFilter style={{ marginRight: 6 }} />
          {trackingHint}
        </Link>
      ) : null}
      <div className="pending-schedules-card__progress">
        {progress.plannedHa != null ? (
          <div className="pending-schedules-card__progress-figures">
            <div>
              <strong>{t("Hectares feitos")}:</strong>{" "}
              {formatHectares(progress.workedHa)} ha
            </div>
            <div>
              <strong>{t("Hectares que faltam")}:</strong>{" "}
              {formatHectares(progress.remainingHa)} ha
            </div>
          </div>
        ) : null}
        <button
          type="button"
          className="pending-schedules-card__map-btn"
          onClick={openOperationMap}
          disabled={!progress.hasTrailTarget}
          title={t("Ver mapeamento da operação")}
          aria-label={t("Ver mapeamento da operação")}
        >
          <FaMapMarkedAlt aria-hidden />
        </button>
      </div>
    </div>
  );
}

function PendingObsCard({
  obs,
  farm_id,
  onAskComplete,
  onEdit,
  focusObsId,
  expandHarvestLoss,
  expandPlanting,
  t,
}) {
  const title = obs.operationName || obs.description || t("Ordem de serviço");
  const isFocused =
    focusObsId != null && Number(obs.id) === Number(focusObsId);

  return (
    <div
      id={`pending-schedules-obs-${obs.id}`}
      className={`${getObsCardClass(obs)} pending-schedules-card${
        isFocused ? " pending-schedules-card--focused" : ""
      }`}
    >
      {obs.operationId ? (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}`}
          className="pending-schedules-operation-link"
        >
          {title}
        </Link>
      ) : (
        <div className="pending-schedules-operation-link pending-schedules-operation-link--static">
          {title}
        </div>
      )}
      {obs.service_type ? (
        <span className="pending-schedules-type-badge">
          {serviceOrderTypeLabel(obs.service_type, t)}
        </span>
      ) : null}
      <ServiceOrderLocationInfo obs={obs} t={t} />
      <ServiceOrderProductsWithCopy obs={obs} t={t} />
      <ObservationExecutionInfo obs={obs} t={t} compact />
      {obs.description ? (
        <div className="pending-schedules-card__description">
          <strong>{t("Observações")}:</strong> {obs.description}
        </div>
      ) : null}
      {Array.isArray(obs.assigned_operators) &&
        obs.assigned_operators.length > 0 && (
          <div className="pending-schedules-card__operators">
            {t("Enviado para")}:{" "}
            {obs.assigned_operators.map((operator) => operator.name).join(", ")}
          </div>
        )}
      {obs.visible_at && (
        <div className="pending-schedules-card__visible-at">
          {t("Visível no app em")}:{" "}
          {new Date(obs.visible_at).toLocaleString("pt-BR")}
        </div>
      )}
      <ServiceOrderPlantingResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandPlanting}
      />
      <ServiceOrderHarvestLossResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandHarvestLoss}
      />
      <ServiceOrderCardActions
        obs={obs}
        farm_id={farm_id}
        onEdit={onEdit}
        onAskComplete={onAskComplete}
        showComplete
        t={t}
      />
      <ScheduledBadge obs={obs} onConfirm={() => onAskComplete(obs)} />
    </div>
  );
}

function CompletedObsCard({
  obs,
  farm_id,
  onEdit,
  onAskComplete,
  focusObsId,
  expandHarvestLoss,
  expandPlanting,
  t,
}) {
  const title = obs.operationName || obs.description || t("Ordem de serviço");
  const isFocused =
    focusObsId != null && Number(obs.id) === Number(focusObsId);

  return (
    <div
      id={`pending-schedules-obs-${obs.id}`}
      className={`${getObsCardClass(obs)} pending-schedules-card${
        isFocused ? " pending-schedules-card--focused" : ""
      }`}
    >
      {obs.operationId ? (
        <Link
          to={`/${farm_id}/operation/${obs.operationId}`}
          className="pending-schedules-operation-link"
        >
          {title}
        </Link>
      ) : (
        <div className="pending-schedules-operation-link pending-schedules-operation-link--static">
          {title}
        </div>
      )}
      {obs.service_type ? (
        <span className="pending-schedules-type-badge">
          {serviceOrderTypeLabel(obs.service_type, t)}
        </span>
      ) : null}
      <ServiceOrderLocationInfo obs={obs} t={t} />
      <ServiceOrderProductsWithCopy obs={obs} t={t} />
      <ObservationExecutionInfo obs={obs} t={t} compact />
      {obs.description ? (
        <div className="pending-schedules-card__description">
          <strong>{t("Observações")}:</strong> {obs.description}
        </div>
      ) : null}
      <ServiceOrderCardActions
        obs={obs}
        farm_id={farm_id}
        onEdit={onEdit}
        onAskComplete={onAskComplete}
        showComplete={false}
        t={t}
      />
      <ServiceOrderPlantingResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandPlanting}
      />
      <ServiceOrderHarvestLossResults
        farmId={farm_id}
        obs={obs}
        t={t}
        defaultExpanded={isFocused && expandHarvestLoss}
      />
    </div>
  );
}

export default withTranslation()(PendingSchedules);
